import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { languageConfig } from "../../../../common/util/langConfig";
import { getCurrentUser } from "../../../profile/state/profileSlice";
import { AppDispatch } from "../../../../common/state/store";
import { fetchHelpArticle, selectHelpArticleEntry } from "../../state/helpArticlesSlice";
import { ILanguage } from "src/model/model";

export interface HelpArticle {
  articleId: string;
  title: string;
  body: string;
  url: string;
}

type HelpArticleStatus = "loading" | "ready" | "none";

interface UseHelpArticleResult {
  status: HelpArticleStatus;
  article: HelpArticle | null;
}

/**
 * The HelpDocs UI language ("en" | "fr") derived from the current user's
 * preference. Shared by the per-node hook and the graph-load bulk prefetch so
 * both cache under the same key.
 */
export const useHelpArticleLang = (): string => {
  const currentUser = useSelector(getCurrentUser);
  return languageConfig((currentUser?.preferred_language as ILanguage)?.name);
};

/**
 * Read the mapped HelpDocs article for a content type from the redux cache,
 * dispatching a fetch if it isn't there yet. The heavy lifting (network + 6h
 * server cache) lives in helpArticlesSlice, and ProjectScenes bulk-prefetches
 * every content type in the graph on load — so by the time a node opens this
 * usually resolves synchronously from cache ("ready"/"none") with no flash. The
 * dispatch here is an idempotent self-heal (the thunk's `condition` skips
 * anything already cached or in flight).
 */
export const useHelpArticle = (modelName: string): UseHelpArticleResult => {
  const dispatch = useDispatch<AppDispatch>();
  const lang = useHelpArticleLang();
  const entry = useSelector(selectHelpArticleEntry(lang, modelName));

  useEffect(() => {
    // No content type yet (node still resolving) → nothing to fetch.
    if (!modelName) return;
    dispatch(fetchHelpArticle({ modelName, lang }));
  }, [dispatch, modelName, lang]);

  if (!modelName) return { status: "none", article: null };
  // Not in cache yet → a fetch is being kicked off by the effect above.
  if (!entry) return { status: "loading", article: null };
  return { status: entry.status, article: entry.article };
};
